// ==================== ABOUT US PAGE ====================

import { motion } from 'framer-motion';
import { 
  Target, 
  Heart, 
  Zap, 
  Users, 
  Award,
  Globe,
  Rocket,
  Shield
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import {
  fadeInUp,
  staggerContainer,
  scaleIn,
  hoverLift
} from '../../utils/animations';
import teamNaeemAhmad from '../../assets/team/naeem-ahmad.webp';
import teamFarrukhShahzad from '../../assets/team/farrukh-shahzad.webp';
import teamQaisAli from '../../assets/team/qais-ali.webp';
import teamMusawir from '../../assets/team/musawir.webp';
import teamUsmanFarooq from '../../assets/team/usman-farooq.webp';

// Team Members Data
const teamMembers = [
  {
    name: 'Naeem Ahmad',
    role: 'CEO & Founder',
    image: teamNaeemAhmad,
    initials: 'NA',
  },
  {
    name: 'Farrukh Shahzad',
    role: 'CTO',
    image: teamFarrukhShahzad,
    initials: 'FS',
  },
  {
    name: 'Qais Ali Abbas',
    role: 'Head of Product',
    image: teamQaisAli,
    initials: 'QA',
  },
  {
    name: 'Musawir Khan',
    role: 'Head of Product',
    image: teamMusawir,
    initials: 'MK',
  },
  {
    name: 'Muhammad Usman Farooq',
    role: 'Head of Marketing',
    image: teamUsmanFarooq,
    initials: 'UF',
  },
];

// Values Data
const values = [
  {
    icon: Target,
    title: 'Customer First',
    description: 'Every decision we make starts with our customers in mind. Their success is our success.',
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'We are passionate about communication and helping businesses connect with their audience.',
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'We constantly push boundaries to deliver cutting-edge solutions that drive results.',
  },
  {
    icon: Shield,
    title: 'Trust',
    description: 'We build trust through transparency, security, and delivering on our promises.',
  },
];

// Stats Data
const stats = [
  { value: '2019', label: 'Founded' },
  { value: '50+', label: 'Team Members' },
  { value: '30+', label: 'Countries' },
  { value: '5,000+', label: 'Happy Clients' },
];

const AboutPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-(--color-signal-orange)/8 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="orange" className="mb-6">About Us</Badge>
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
            >
              Transforming Business
              <span className="text-gradient block mt-2">Communication</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-(--color-soft-gray) leading-relaxed"
            >
              We're on a mission to help businesses connect with their customers in meaningful ways through the power of WhatsApp automation.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-heading font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-(--color-muted-gray)">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Badge variant="orange" className="mb-4">Our Story</Badge>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
                From Idea to
                <span className="text-gradient"> Industry Leader</span>
              </h2>
              <div className="space-y-4 text-(--color-soft-gray) leading-relaxed">
                <p>
                  AmplifyMsg was born in 2019 from a simple observation: businesses were struggling to keep up with the changing landscape of customer communication. While consumers were embracing messaging apps, businesses were still relying on outdated channels.
                </p>
                <p>
                  Our founders, seasoned entrepreneurs with backgrounds in telecommunications and SaaS, saw an opportunity to bridge this gap. They envisioned a platform that would make WhatsApp Business API accessible to companies of all sizes.
                </p>
                <p>
                  Today, AmplifyMsg powers communication for over 5,000 businesses across 30+ countries, processing millions of messages daily. But we're just getting started.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
            >
              <div className="relative bg-(--color-charcoal-black) rounded-2xl p-8 border border-(--color-border-gray)">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { icon: Globe, label: 'Global Reach' },
                    { icon: Rocket, label: 'Fast Growth' },
                    { icon: Users, label: 'Amazing Team' },
                    { icon: Award, label: 'Award Winning' },
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-(--color-orange-glow) flex items-center justify-center">
                        <item.icon size={24} className="text-(--color-signal-orange)" />
                      </div>
                      <p className="text-sm text-(--color-soft-gray)">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-(--color-signal-orange)/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-(--color-warm-amber)/10 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Mission & Vision</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              What Drives Us
              <span className="text-gradient"> Forward</span>
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} whileHover={hoverLift}>
              <Card variant="highlight" className="h-full">
                <div className="w-12 h-12 rounded-xl bg-(--color-signal-orange) flex items-center justify-center mb-6">
                  <Target size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-4">Our Mission</h3>
                <p className="text-(--color-soft-gray) leading-relaxed">
                  To empower businesses of all sizes to build meaningful connections with their customers through intelligent, automated messaging solutions that drive growth and enhance customer experiences.
                </p>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp} whileHover={hoverLift}>
              <Card variant="highlight" className="h-full">
                <div className="w-12 h-12 rounded-xl bg-(--color-warm-amber) flex items-center justify-center mb-6">
                  <Rocket size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-4">Our Vision</h3>
                <p className="text-(--color-soft-gray) leading-relaxed">
                  To become the world's leading WhatsApp Business API platform, setting the standard for conversational commerce and making powerful communication tools accessible to every business.
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Our Values</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              The Principles That
              <span className="text-gradient"> Guide Us</span>
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {values.map((value, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={hoverLift}>
                <Card hoverable className="h-full text-center">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-(--color-orange-glow) flex items-center justify-center mb-4">
                    <value.icon size={28} className="text-(--color-signal-orange)" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-(--color-muted-gray) leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Our Team</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Meet the People
              <span className="text-gradient"> Behind AmplifyMsg</span>
            </h2>
            <p className="text-(--color-muted-gray)">
              A passionate team dedicated to transforming business communication.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 lg:grid-cols-5 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -10 }}
                className="text-center"
              >
                <div className="w-32 h-32 mx-auto mb-4 p-1 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      loading="lazy"
                      className="w-full h-full rounded-full object-cover object-top"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-white font-heading">
                      {member.initials}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-heading font-semibold text-white mb-1">
                  {member.name}
                </h3>
                <p className="text-sm text-(--color-signal-orange)">{member.role}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { AboutPage };

